/**
 * eKYB 商户 API
 * 已切换至 Client KYB 合规接口（/client/compliance/kyb），通过 adapter 保持对外签名兼容
 */
import type {
  MerchantInfoResponse,
  PostOpeningRequest,
  PostOpeningResponse,
} from './types';
import { getClientKybInfo, postClientKybAdd, postClientKybUpdate } from './clientKybService';
import {
  segmentVoToMerchantInfo,
  merchantInfoToSegmentDto,
} from './clientKybAdapter';
import { request } from '@umijs/max';
import {
  getActiveLoginAccount,
  getKybIdForAccount,
  getResolvedKybIdForRequest,
  setKybIdForAccount,
} from '@/utils/clientKybSession';

/**
 * 获取商户详情
 * id 优先级：入参 > localStorage 中当前登录账号绑定的 kybId > 'current'
 */
export async function getMerchantInfo(options?: { id?: string }): Promise<MerchantInfoResponse> {
  const id = getResolvedKybIdForRequest(options?.id);
  const resp = await getClientKybInfo({ id });
  if (resp?.status !== '1' || !resp.data) {
    return { status: resp?.status, message: resp?.message };
  }
  const data = segmentVoToMerchantInfo(resp.data);
  return { status: '1', data, message: resp.message };
}

/**
 * 入网开户提交
 * 有 id 时调用 postClientKybUpdate，否则 postClientKybAdd；fillStatus 仍透传至 query（若后端支持）
 */
export async function postOpening(data: PostOpeningRequest & { fillStatus?: string }) {
  const { fillStatus, ...body } = data;
  const dto = merchantInfoToSegmentDto(body as Record<string, any>);
  if (!dto.id) {
    const acc = getActiveLoginAccount();
    const bound = acc ? getKybIdForAccount(acc) : undefined;
    if (bound) dto.id = bound;
  }
  const hasId = !!dto.id;
  const opts = fillStatus ? { fillStatus } : undefined;
  const resp = hasId
    ? await postClientKybUpdate(dto, opts)
    : await postClientKybAdd(dto, opts);
  if (resp?.status === '1' && resp.data) {
    const data = segmentVoToMerchantInfo(resp.data);
    const acc = getActiveLoginAccount();
    if (acc && data?.id && !getKybIdForAccount(acc)) {
      setKybIdForAccount(acc, data.id);
    }
    return { status: '1', data: data as any, message: resp.message } as PostOpeningResponse;
  }
  return { status: resp?.status, message: resp?.message } as PostOpeningResponse;
}

/** 代理入网申请注册-提醒（仍走原接口，非 Client KYB 范围） */
export async function postOpeningRemind() {
  return request<{ data?: Record<string, unknown>; message?: string; status?: string }>(
    '/api/merchant/opening/remind',
    { method: 'POST' },
  );
}
